import { useState, useEffect } from 'react';
import api from '../helpers/api';

/**
 * Custom hook to fetch common reference data used across work order pages
 * Fetches locations, departments, manufacturers, technicians, and tools
 *
 * @param {number} organizationId - The organization ID to fetch data for
 * @param {object} options - Configuration options
 * @param {boolean} options.fetchLocations - Whether to fetch locations (default: true)
 * @param {boolean} options.fetchDepartments - Whether to fetch departments (default: true)
 * @param {boolean} options.fetchManufacturers - Whether to fetch manufacturers (default: true)
 * @param {boolean} options.fetchTechnicians - Whether to fetch technicians (default: true)
 * @param {boolean} options.fetchTools - Whether to fetch tools (default: false)
 *
 * @returns {object} Reference data and loading state
 */
export const useReferenceData = (organizationId, options = {}) => {
  const {
    fetchLocations = true,
    fetchDepartments = false, // Disabled - single Operations department used
    fetchManufacturers = true,
    fetchTechnicians = true,
    fetchTools = false,
  } = options;

  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!organizationId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const promises = [];

        if (fetchLocations) {
          promises.push(
            api.get(`organization-location/get-by-organization-id?organizationId=${organizationId}&count=9999&hierarchy=false`)
              .then(({ data }) => setLocations(data.response[0]?.locations || []))
              .catch(err => {
                console.error('Error fetching locations:', err);
                setLocations([]);
              })
          );
        }

        if (fetchDepartments) {
          promises.push(
            api.get('department/all')
              .then(({ data }) => setDepartments(data.response || []))
              .catch(err => {
                console.error('Error fetching departments:', err);
                setDepartments([]);
              })
          );
        }

        if (fetchManufacturers) {
          promises.push(
            api.get('asset/get-manufacturer')
              .then(({ data }) => setManufacturers(data.response?.manufacturers || []))
              .catch(err => {
                console.error('Error fetching manufacturers:', err);
                setManufacturers([]);
              })
          );
        }

        if (fetchTechnicians) {
          // Uses RBAC user_roles table via user/technicians endpoint
          promises.push(
            api.get(`user/technicians`, { params: { organizationId } })
              .then(({ data }) => setTechnicians(data?.response || []))
              .catch(err => {
                console.error('Error fetching technicians:', err);
                setTechnicians([]);
              })
          );
        }

        if (fetchTools) {
          promises.push(
            api.get(`tool/all?organizationId=${organizationId}&count=9999`)
              .then(({ data }) => setTools(data?.response || []))
              .catch(err => {
                console.error('Error fetching tools:', err);
                setTools([]);
              })
          );
        }

        await Promise.all(promises);
      } catch (err) {
        console.error('Error fetching reference data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [organizationId, fetchLocations, fetchDepartments, fetchManufacturers, fetchTechnicians, fetchTools]);

  return {
    locations,
    departments,
    manufacturers,
    technicians,
    tools,
    loading,
    error,
  };
};

export default useReferenceData;
